import { Button } from './ui/button';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

export const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const heroImage = 'https://images.unsplash.com/photo-1612607696387-f139f76bdd6c?w=1920&q=80';

  return (
    <section className="relative min-h-screen flex items-center justify-start overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={heroImage} alt="Next Level Hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/30" />
      </div>
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#C9A84C]/40 to-transparent z-10" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
        <div className="max-w-3xl">
          <div className={`inline-flex items-center gap-2 mb-8 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="h-[1px] w-8 bg-[#C9A84C]" />
            <span className="text-[#C9A84C] text-sm font-medium tracking-[0.2em] uppercase">{t.hero.badge}</span>
          </div>
          <h1 className={`text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-[#F5F0E8] leading-[1.05] mb-8 font-heading transition-all duration-700 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {t.hero.headline.split(' ').map((word, i) => {
              const lw = word.toLowerCase().replace(/[.,!?]/g, '');
              const highlights = t.hero.highlightWords || ['next', 'level', 'gurzì', 'gurzi', 'motionlab', 'nächstes', 'nivel', 'niveau', 'livello', 'poziom', 'razina', 'úroveň', 'ниво', 'рівень', 'уровень', '레벨', '层次'];
              const isGold = highlights.some(h => lw.includes(h.toLowerCase()));
              return <span key={i} className={isGold ? 'text-[#C9A84C]' : ''}>{word}{' '}</span>;
            })}
          </h1>
          <p className={`text-lg sm:text-xl text-[#9A9590] max-w-2xl mb-10 leading-relaxed transition-all duration-700 delay-400 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>{t.hero.subline}</p>
          <div className={`flex items-center gap-3 mb-12 transition-all duration-700 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-[2px] h-12 bg-[#C9A84C]/40" />
            <div>
              <p className="text-[#F5F0E8]/70 italic text-sm">"{t.hero.founderQuote}"</p>
              <p className="text-[#C9A84C] text-xs mt-1 font-medium">– {t.hero.founderName}, {t.hero.founderRole}</p>
            </div>
          </div>
          <div className={`flex flex-wrap gap-4 transition-all duration-700 delay-[600ms] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <Button onClick={() => scrollTo('#kontakt')} className="bg-[#C9A84C] hover:bg-[#D4B85A] text-[#0A0A0A] font-semibold px-8 h-13 text-base tracking-wide border-0 shadow-lg shadow-[#C9A84C]/20">{t.hero.ctaPrimary}</Button>
            <Button onClick={() => scrollTo('#pillars')} variant="outline" className="border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]/50 font-medium px-8 h-13 text-base tracking-wide bg-transparent">{t.hero.ctaSecondary}</Button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <ChevronDown className="text-[#C9A84C]/50" size={24} />
      </div>
    </section>
  );
};
